import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useAuth } from '../../contexts/AuthContext';

gsap.registerPlugin(ScrollTrigger);

/* ─────────────────────────────────────────────────────────────────
 * Shared design tokens
 * ───────────────────────────────────────────────────────────────── */
const T = {
  ink: '#0A0A0A',
  inkSoft: '#52525B',
  inkMute: '#A1A1AA',
  hair: '#E4E4E7',
  cream: '#F7F6F2',
  white: '#FFFFFF',
  violet: '#7C3AED',
  violetSoft: '#A78BFA',
  violetDark: '#5B21B6',
};

const SERIF  = '"Nasalization", Georgia, serif';
const SANS   = '"Inter", system-ui, sans-serif';
const MONO   = '"JetBrains Mono", monospace';

// SectionLabels removed as per design request

/* Reusable scroll-up reveal */
function useReveal(ref, opts = {}) {
  useEffect(() => {
    if (!ref.current) return;
    const targets = ref.current.querySelectorAll('[data-reveal]');
    const ctx = gsap.context(() => {
      gsap.fromTo(targets,
        { autoAlpha: 0, y: 50 },
        {
          autoAlpha: 1, y: 0,
          duration: 1.0,
          ease: 'power3.out',
          stagger: opts.stagger ?? 0.08,
          scrollTrigger: {
            trigger: ref.current,
            start: opts.start ?? 'top 75%',
            once: true,
          },
        });
    }, ref);
    return () => ctx.revert();
  }, [ref, opts.start, opts.stagger]);
}

/* ═════════════════════════════════════════════════════════════════
 * 1. MANIFESTO — Editorial intro paragraph + signature
 * ═════════════════════════════════════════════════════════════════ */
export function ManifestoSection() {
  const ref = useRef(null);
  useReveal(ref);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section ref={ref} style={{
      background: 'transparent',
      padding: isMobile ? '80px 24px' : 'clamp(120px, 16vw, 220px) clamp(28px, 6vw, 96px)',
      position: 'relative',
    }}>
      <div style={{
        maxWidth: '1320px', margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,2.4fr)',
        gap: isMobile ? '24px' : 'clamp(32px, 6vw, 96px)',
        alignItems: 'flex-start',
      }}>
        <div data-reveal>
          {/* SectionLabel removed */}
        </div>

        <div>
          <p data-reveal style={{
            fontFamily: SERIF,
            fontWeight: 300,
            fontStyle: 'italic',
            fontSize: 'clamp(28px, 4vw, 56px)',
            lineHeight: 1.18,
            letterSpacing: '-0.018em',
            color: T.white,
            margin: 0,
            fontVariationSettings: '"opsz" 144, "SOFT" 50',
          }}>
            We believe the next great wave of the internet won't be built by
            those who own the tools — it will be built by those who own
            <span style={{ color: T.violetSoft, fontWeight: 400 }}> their creations</span>.
          </p>
          <p data-reveal style={{
            fontFamily: SANS,
            fontSize: 'clamp(15px, 1.2vw, 18px)',
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.7)',
            margin: '40px 0 0',
            maxWidth: '560px',
            fontWeight: 400,
          }}>
            DAGGPT is the unified creative operating system for generative
            intelligence. Every prompt, every render, every line of code —
            cryptographically anchored to DAGCHAIN. Provenance is not a feature.
            It is the foundation.
          </p>
          <div data-reveal style={{
            marginTop: '48px',
            display: 'flex', alignItems: 'center', gap: '14px',
          }}>
            <div style={{ width: 40, height: '1px', background: T.white }} />
            <span style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: '15px', color: T.white,
            }}>The DAGGPT Team</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════════════════════════════════════════════
 * 2. CAPABILITIES — Big serif headline + ticking marquee strip
 * ═════════════════════════════════════════════════════════════════ */
export function CapabilitiesSection() {
  const ref = useRef(null);
  useReveal(ref);
  const items = [
    'Chat', 'Image Generation', 'Music', 'Video', 'Code', 'Smart Contracts',
    'Voice Cloning', '3D Assets', 'Research', 'Data Analysis', 'Translation', 'Agents',
  ];
  return (
    <section ref={ref} style={{
      background: 'transparent',
      padding: 'clamp(100px, 14vw, 180px) 0',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ padding: '0 clamp(28px, 6vw, 96px)', maxWidth: '1320px', margin: '0 auto' }}>
        <div data-reveal>
          {/* SectionLabel removed */}
        </div>
        <h2 data-reveal style={{
          fontFamily: SERIF,
          fontWeight: 300,
          fontSize: 'clamp(2.6rem, 6.5vw, 7.5rem)',
          lineHeight: 0.96,
          letterSpacing: '-0.035em',
          color: T.white,
          margin: '0 0 16px',
          maxWidth: '15ch',
        }}>
          Every modality.
          <br />
          <em style={{
            fontStyle: 'italic',
            fontWeight: 400,
            color: T.violetSoft,
            display: 'inline-block',
            paddingRight: '0.12em',
            paddingBottom: '0.08em',
          }}>One canvas.</em>
        </h2>
        <p data-reveal style={{
          fontFamily: SANS,
          fontSize: 'clamp(15px, 1.2vw, 17px)',
          lineHeight: 1.65,
          color: 'rgba(255,255,255,0.7)',
          maxWidth: '520px',
          margin: '24px 0 0',
        }}>
          Chat with frontier LLMs. Generate images, music, video.
          Write and audit smart contracts. Ship it all from a single
          pane of glass — and own every output.
        </p>
      </div>

      {/* Marquee — two opposing rows */}
      <div data-reveal style={{ marginTop: '96px' }}>
        <MarqueeRow items={items} reverse={false} />
        <div style={{ height: '12px' }} />
        <MarqueeRow items={items.slice().reverse()} reverse={true} accent />
      </div>
    </section>
  );
}

function MarqueeRow({ items, reverse, accent }) {
  const dup = [...items, ...items, ...items];
  return (
    <div style={{
      overflow: 'hidden',
      borderTop: `1px solid rgba(255,255,255,0.08)`,
      borderBottom: `1px solid rgba(255,255,255,0.08)`,
      padding: '24px 0',
    }}>
      <div style={{
        display: 'flex',
        gap: '64px',
        whiteSpace: 'nowrap',
        animation: `marquee-${reverse ? 'r' : 'l'} 38s linear infinite`,
        width: 'max-content',
      }}>
        {dup.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '32px',
          }}>
            <span style={{
              fontFamily: SERIF,
              fontStyle: accent ? 'italic' : 'normal',
              fontWeight: accent ? 400 : 300,
              fontSize: 'clamp(2.4rem, 5vw, 5rem)',
              letterSpacing: '-0.025em',
              color: accent ? T.violetSoft : T.white,
              lineHeight: 1,
            }}>{item}</span>
            <span style={{
              fontFamily: MONO,
              color: 'rgba(255,255,255,0.2)',
              fontSize: '14px',
            }}>✦</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-l { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }
        @keyframes marquee-r { from { transform: translateX(-33.333%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
 * 3. MODELS — Horizontal pinned scroll of frontier model cards
 * ═════════════════════════════════════════════════════════════════ */
const MODELS = [
  { name: 'Claude 3.5 Sonnet', by: 'Anthropic',  tag: 'Reasoning · Vision · Code',     score: 98 },
  { name: 'GPT-4o',            by: 'OpenAI',      tag: 'Multimodal · Realtime',         score: 96 },
  { name: 'Gemini 1.5 Pro',    by: 'Google',      tag: '2M ctx · Multimodal',           score: 94 },
  { name: 'Llama 3.1 405B',    by: 'Meta',        tag: 'Open-source flagship',          score: 91 },
  { name: 'DeepSeek R1',       by: 'DeepSeek',    tag: 'Pure reasoning',                score: 93 },
  { name: 'Mistral Large 2',   by: 'Mistral',     tag: 'Multilingual · Function',       score: 89 },
  { name: 'Grok 2',            by: 'xAI',         tag: 'Real-time · Web',                score: 92 },
  { name: 'Suno v4',           by: 'Suno',        tag: 'Music generation',              score: 86 },
  { name: 'Kling 1.6',         by: 'Kuaishou',    tag: 'Cinematic video',                score: 88 },
  { name: 'Flux Pro 1.1',      by: 'Black Forest',tag: 'Photoreal imagery',             score: 95 },
];

export function ModelsSection({ onSignInClick }) {
  const { isAuthenticated } = useAuth();
  const sectionRef = useRef(null);
  const trackRef   = useRef(null);
  const headRef    = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(headRef.current?.querySelectorAll('[data-reveal]') || [],
        { autoAlpha: 0, y: 40 },
        { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 75%', once: true } });

      const getEnd = () => -(track.scrollWidth - window.innerWidth + 96);
      gsap.to(track, {
        x: getEnd,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => `+=${Math.abs(getEnd())}`,
          scrub: 0.6,
          pin: true,
          invalidateOnRefresh: true,
          onRefresh: (self) => {
            window._modelsPinTotal = Math.abs(getEnd());
          },
          onUpdate: (self) => {
            window._modelsPinOffset = self.progress * Math.abs(getEnd());
          },
        },
      });
    }, section);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} style={{
      background: 'transparent', overflow: 'hidden',
      borderTop: `1px solid rgba(255,255,255,0.08)`,
    }}>
      <div ref={headRef} style={{
        padding: 'clamp(100px, 14vw, 160px) clamp(28px, 6vw, 96px) 64px',
        maxWidth: '1320px', margin: '0 auto',
      }}>
        <div data-reveal>
          {/* SectionLabel removed */}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '40px',
          flexWrap: 'wrap',
        }}>
          <h2 data-reveal style={{
            fontFamily: SERIF,
            fontWeight: 300,
            fontSize: 'clamp(2.6rem, 6.5vw, 7rem)',
            lineHeight: 0.95,
            letterSpacing: '-0.035em',
            color: T.white,
            margin: 0,
            maxWidth: '14ch',
          }}>
            400+ frontier models.&nbsp;
            <em style={{ color: T.violet, fontStyle: 'italic', fontWeight: 400, display: 'inline-block', paddingRight: '0.12em', paddingBottom: '0.08em' }}>
              Zero friction.
            </em>
          </h2>
          <p data-reveal style={{
            fontFamily: SANS,
            fontSize: '16px',
            lineHeight: 1.65,
            color: 'rgba(255,255,255,0.7)',
            maxWidth: '360px',
            margin: 0,
          }}>
            Switch models mid-conversation. No API keys. No accounts.
            Every output is signed, hashed, and anchored to DAGCHAIN.
          </p>
        </div>
      </div>

      <div style={{ paddingLeft: 'clamp(28px, 6vw, 96px)', paddingBottom: '120px' }}>
        <div ref={trackRef} style={{
          display: 'flex', gap: '20px',
          width: 'max-content',
        }}>
          {MODELS.map((m, i) => <ModelCard key={m.name} model={m} index={i} />)}
          <ModelEndCard onSignInClick={onSignInClick} isAuthenticated={isAuthenticated} />
        </div>
      </div>
    </section>
  );
}

function ModelCard({ model, index }) {
  return (
    <article style={{
      width: 'clamp(280px, 24vw, 340px)',
      flexShrink: 0,
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid rgba(255,255,255,0.08)`,
      borderRadius: '20px',
      padding: '28px',
      display: 'flex', flexDirection: 'column',
      gap: '20px',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s',
      cursor: 'default',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = 'translateY(-6px)';
      e.currentTarget.style.borderColor = T.violet;
      e.currentTarget.style.boxShadow = '0 24px 48px -12px rgba(124,58,237,0.18)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = 'none';
      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontFamily: MONO, fontSize: '11px', fontWeight: 600,
          letterSpacing: '0.18em', color: T.inkMute,
        }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <span style={{
          fontFamily: MONO, fontSize: '10px', fontWeight: 600,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          padding: '4px 10px', borderRadius: '999px',
          background: 'rgba(124,58,237,0.1)',
          color: '#D8B4FE',
        }}>{model.by}</span>
      </div>

      <div>
        <h3 style={{
          fontFamily: SERIF,
          fontStyle: 'italic',
          fontWeight: 400,
          fontSize: 'clamp(1.6rem, 1.9vw, 2rem)',
          lineHeight: 1.05,
          letterSpacing: '-0.02em',
          color: T.white,
          margin: '0 0 8px',
        }}>{model.name}</h3>
        <p style={{
          fontFamily: SANS, fontSize: '13px',
          color: 'rgba(255,255,255,0.7)', margin: 0,
          fontWeight: 400,
        }}>{model.tag}</p>
      </div>

      <div style={{ marginTop: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{
            fontFamily: MONO, fontSize: '9px', fontWeight: 600,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: T.inkMute,
          }}>Performance</span>
          <span style={{
            fontFamily: MONO, fontSize: '11px', fontWeight: 700,
            color: T.violet,
          }}>{model.score}</span>
        </div>
        <div style={{ height: '3px', background: 'rgba(255,255,255,0.1)', borderRadius: '99px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${model.score}%`,
            background: `linear-gradient(90deg, ${T.violet}, ${T.violetSoft})`,
          }} />
        </div>
      </div>
    </article>
  );
}

function ModelEndCard({ onSignInClick, isAuthenticated }) {
  return (
    <div style={{
      width: 'clamp(280px, 24vw, 340px)',
      flexShrink: 0,
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '20px',
      padding: '28px',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between',
      color: T.white,
    }}>
      <span style={{
        fontFamily: MONO, fontSize: '10px', fontWeight: 600,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: T.violetSoft,
      }}>+ 30 More</span>
      <h3 style={{
        fontFamily: SERIF,
        fontStyle: 'italic', fontWeight: 400,
        fontSize: 'clamp(2rem, 2.6vw, 2.6rem)',
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        color: T.white, margin: 0,
      }}>And the entire frontier, always up to date.</h3>
      <a href="/workspace" style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px',
        fontFamily: SANS, fontSize: '14px', fontWeight: 600,
        color: T.violetSoft, textDecoration: 'none',
        marginTop: '24px',
      }}
      onClick={(e) => {
        if (!isAuthenticated) {
          e.preventDefault();
          if (onSignInClick) onSignInClick('/dashboard');
        }
      }}
      >
        Browse all models
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
 * 4. PROVENANCE — Dark interlude. Terminal mock + on-chain promise.
 * ═════════════════════════════════════════════════════════════════ */
export function ProvenanceSection({ onSignInClick }) {
  const { isAuthenticated } = useAuth();
  const ref = useRef(null);
  useReveal(ref, { stagger: 0.12 });
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* Hash typewriter */
  const hashRef = useRef(null);
  useEffect(() => {
    const hash = '0x9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08';
    let i = 0;
    const id = setInterval(() => {
      if (!hashRef.current) return;
      hashRef.current.textContent = hash.slice(0, i) + '▊';
      i = (i + 1) % (hash.length + 1);
    }, 65);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={ref} style={{
      background: 'transparent',
      padding: isMobile ? '80px 24px' : 'clamp(120px, 14vw, 180px) clamp(28px, 6vw, 96px)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        width: '900px', height: '500px',
        background: 'radial-gradient(ellipse, rgba(124,58,237,0.18) 0%, transparent 70%)',
        pointerEvents: 'none', filter: 'blur(40px)',
      }} />

      <div style={{
        maxWidth: '1320px', margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1.1fr)',
        gap: isMobile ? '32px' : 'clamp(40px, 6vw, 100px)',
        alignItems: 'center',
        position: 'relative', zIndex: 2,
      }}>
        <div>
          <div data-reveal style={{ display: 'inline-block' }}>
            {/* SectionLabel removed */}
          </div>
          <h2 data-reveal style={{
            fontFamily: SERIF,
            fontWeight: 300,
            fontSize: 'clamp(2.6rem, 6vw, 6.5rem)',
            lineHeight: 0.97,
            letterSpacing: '-0.035em',
            color: T.white,
            margin: '0 0 28px',
          }}>
            Every creation,
            <br />
            <em style={{
              fontStyle: 'italic', fontWeight: 400,
              background: `linear-gradient(135deg, ${T.violetSoft}, ${T.violet})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              paddingRight: '0.12em',
              paddingBottom: '0.08em',
            }}>immutably yours.</em>
          </h2>
          <p data-reveal style={{
            fontFamily: SANS,
            fontSize: 'clamp(15px, 1.2vw, 18px)',
            lineHeight: 1.7,
            color: 'rgba(255,255,255,0.55)',
            maxWidth: '480px', margin: '0 0 40px',
            fontWeight: 400,
          }}>
            Outputs from DAGGPT are SHA-256 hashed and broadcast to DAGCHAIN —
            a Layer-1 DAG-based ledger. Sub-second finality, zero gas friction,
            permanent provenance.
          </p>
          <a href="/workspace" data-reveal style={{
            display: 'inline-flex', alignItems: 'center', gap: '10px',
            padding: '14px 30px', borderRadius: '999px',
            background: T.white, color: T.ink,
            fontFamily: SANS, fontSize: '14px', fontWeight: 600,
            textDecoration: 'none',
            transition: 'background 0.25s, transform 0.25s',
          }}
          onClick={(e) => {
            if (!isAuthenticated) {
              e.preventDefault();
              if (onSignInClick) onSignInClick('/dashboard');
            }
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#EDE9FE'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = T.white; e.currentTarget.style.transform = 'none'; }}
          >
            Create & Anchor
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>

        <div data-reveal style={{
          background: 'rgba(0,0,0,0.5)',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.07)',
          overflow: 'hidden',
          boxShadow: '0 32px 80px -20px rgba(0,0,0,0.6)',
        }}>
          <div style={{
            padding: '14px 18px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'rgba(0,0,0,0.6)',
          }}>
            {['#FF5F56','#FFBD2E','#27C93F'].map(c => (
              <div key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />
            ))}
            <span style={{
              marginLeft: '10px',
              fontFamily: MONO, fontSize: '11px',
              color: 'rgba(255,255,255,0.4)',
            }}>~/dagchain — provenance</span>
          </div>
          <div style={{
            padding: isMobile ? '16px' : '24px 22px',
            fontFamily: MONO, fontSize: isMobile ? '10.5px' : '12.5px', lineHeight: 2,
          }}>
            {[
              { c: '#71717A', t: '$ daggpt deploy --asset ./hero.png' },
              { c: '#71717A', t: '> sha-256 ...' },
              { c: T.violetSoft, t: '> hash 0x9f86d081...0f00a08' },
              { c: '#71717A', t: '> connecting to DAGCHAIN L1 ...' },
              { c: '#71717A', t: '> broadcasting to 2,847 nodes ...' },
              { c: '#22C55E', t: '✓ anchored — block #1,492,087 — 0.41s' },
              { c: T.violetSoft, t: '✓ owner 0x4D2a...F3b1 — IMMUTABLE' },
            ].map((l, i) => <div key={i} style={{ color: l.c }}>{l.t}</div>)}
            <div ref={hashRef} style={{
              marginTop: '16px',
              padding: '12px 14px',
              background: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.18)',
              borderRadius: '8px',
              color: T.violet, wordBreak: 'break-all',
              fontSize: '11px',
              letterSpacing: '0.02em',
            }} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════════════════════════════════════════════
 * 5. HOW IT WORKS — 4-step pipeline
 * ═════════════════════════════════════════════════════════════════ */
const STEPS = [
  { n: '01', title: 'Create',  body: 'Open the workspace. Pick a modality. Generate with the model of your choice — chat, image, music, video, code, contract.' },
  { n: '02', title: 'Hash',    body: 'Every output is fingerprinted by SHA-256 the instant it is rendered. Tamper-evident from birth.' },
  { n: '03', title: 'Anchor',  body: 'The fingerprint is broadcast to DAGCHAIN — Layer-1 DAG ledger. Final in under one second. No gas.' },
  { n: '04', title: 'Prove',   body: 'Share a verifiable certificate. Authenticate authorship, ownership, and timestamp — forever.' },
];

export function HowItWorksSection() {
  const ref = useRef(null);
  useReveal(ref);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section ref={ref} style={{
      background: 'transparent',
      padding: isMobile ? '80px 24px' : 'clamp(120px, 14vw, 180px) clamp(28px, 6vw, 96px)',
    }}>
      <div style={{ maxWidth: '1320px', margin: '0 auto' }}>
        <div data-reveal>
          {/* SectionLabel removed */}
        </div>
        <h2 data-reveal style={{
          fontFamily: SERIF,
          fontWeight: 300,
          fontSize: 'clamp(2.6rem, 6vw, 6.5rem)',
          lineHeight: 0.95,
          letterSpacing: '-0.035em',
          color: T.white,
          margin: '0 0 80px',
          maxWidth: '14ch',
        }}>
          Create.&nbsp;Hash.&nbsp;
          <em style={{ fontStyle: 'italic', fontWeight: 400, color: T.violet, display: 'inline-block', paddingRight: '0.12em', paddingBottom: '0.08em' }}>Prove.</em>
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '0',
          borderTop: `1px solid rgba(255,255,255,0.08)`,
        }}>
          {STEPS.map((s, i) => (
            <div key={s.n} data-reveal style={{
              borderBottom: `1px solid rgba(255,255,255,0.08)`,
              borderRight: (!isMobile && i < STEPS.length - 1) ? `1px solid rgba(255,255,255,0.08)` : 'none',
              padding: isMobile ? '32px 16px' : '40px 32px 56px',
              minHeight: isMobile ? 'auto' : '320px',
              display: 'flex', flexDirection: 'column',
              gap: '24px',
              transition: 'background 0.3s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{
                fontFamily: MONO,
                fontSize: '11px', fontWeight: 600,
                letterSpacing: '0.22em', color: T.violet,
              }}>{s.n}</div>
              <h3 style={{
                fontFamily: SERIF,
                fontStyle: 'italic', fontWeight: 400,
                fontSize: 'clamp(2rem, 3vw, 3rem)',
                lineHeight: 1, letterSpacing: '-0.025em',
                color: T.white, margin: 0,
              }}>{s.title}</h3>
              <p style={{
                fontFamily: SANS,
                fontSize: '14px',
                lineHeight: 1.65,
                color: 'rgba(255,255,255,0.7)',
                margin: 'auto 0 0',
                maxWidth: '32ch',
                fontWeight: 400,
              }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════════════════════════════════════════════
 * 6. NUMBERS — Big editorial stat row
 * ═════════════════════════════════════════════════════════════════ */
const STATS = [
  { v: '400+',  l: 'Frontier Models' },
  { v: '100%',  l: 'On-Chain Outputs' },
  { v: '0.4s',  l: 'Block Finality' },
  { v: '∞',     l: 'Provenance Lifetime' },
];

export function NumbersSection() {
  const ref = useRef(null);
  useReveal(ref, { stagger: 0.12 });
  return (
    <section ref={ref} style={{
      background: 'transparent',
      padding: 'clamp(80px, 10vw, 140px) clamp(28px, 6vw, 96px)',
      borderTop: `1px solid rgba(255,255,255,0.08)`,
      borderBottom: `1px solid rgba(255,255,255,0.08)`,
    }}>
      <div style={{
        maxWidth: '1320px', margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'clamp(40px, 6vw, 96px)',
      }}>
        {STATS.map((s) => (
          <div key={s.l} data-reveal>
            <div style={{
              fontFamily: SERIF,
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: 'clamp(3.5rem, 6vw, 7rem)',
              lineHeight: 0.92,
              letterSpacing: '-0.05em',
              color: T.white,
              marginBottom: '14px',
            }}>{s.v}</div>
            <div style={{
              fontFamily: MONO,
              fontSize: '11px', fontWeight: 600,
              letterSpacing: '0.24em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
            }}>{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═════════════════════════════════════════════════════════════════
 * 7. CTA + FOOTER
 * ═════════════════════════════════════════════════════════════════ */
export function CtaFooterSection({ onSignInClick }) {
  const { isAuthenticated } = useAuth();
  const ref = useRef(null);
  useReveal(ref);
  return (
    <>
      <section ref={ref} style={{
        background: 'transparent',
        padding: 'clamp(140px, 18vw, 240px) clamp(28px, 6vw, 96px)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '20%', right: '-15%',
          width: '700px', height: '700px',
          background: 'radial-gradient(circle, rgba(124,58,237,0.10) 0%, transparent 60%)',
          pointerEvents: 'none', filter: 'blur(60px)',
        }} />

        <div style={{ maxWidth: '1320px', margin: '0 auto', position: 'relative' }}>
          <div data-reveal>
            {/* SectionLabel removed */}
          </div>
          <h2 data-reveal style={{
            fontFamily: SERIF,
            fontWeight: 300,
            fontSize: 'clamp(4rem, 12vw, 14rem)',
            lineHeight: 0.86,
            letterSpacing: '-0.05em',
            color: T.white,
            margin: '0 0 48px',
          }}>
            Create with
            <br />
            <em style={{
              fontStyle: 'italic', fontWeight: 400,
              background: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft}, ${T.violetDark})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              paddingRight: '0.12em',
              paddingBottom: '0.08em',
            }}>
              DAGGPT.
            </em>
          </h2>

          <div data-reveal style={{
            display: 'flex', alignItems: 'flex-end',
            justifyContent: 'space-between', flexWrap: 'wrap',
            gap: '32px',
          }}>
            <p style={{
              fontFamily: SANS,
              fontSize: 'clamp(15px, 1.3vw, 18px)',
              lineHeight: 1.65,
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '440px', margin: 0,
              fontWeight: 400,
            }}>
              The unified creative OS for generative intelligence.
              No waitlist. No setup. Just build.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <a href="/workspace" style={ctaPrimary}
                onClick={(e) => {
                  if (!isAuthenticated) {
                    e.preventDefault();
                    if (onSignInClick) onSignInClick('/dashboard');
                  }
                }}
                onMouseEnter={e => { e.currentTarget.style.background = T.violet; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = T.ink; e.currentTarget.style.transform = 'none'; }}>
                Enter DAGGPT
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a href="https://docs.daggpt.network" style={ctaSecondary}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.violet; e.currentTarget.style.color = T.violet; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(10,10,10,0.15)'; e.currentTarget.style.color = T.ink; e.currentTarget.style.transform = 'none'; }}>
                Read Docs
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

const ctaPrimary = {
  display: 'inline-flex', alignItems: 'center', gap: '10px',
  padding: '18px 38px', borderRadius: '999px',
  background: T.ink, color: T.white,
  fontFamily: SANS, fontSize: '15px', fontWeight: 600,
  textDecoration: 'none', letterSpacing: '-0.005em',
  transition: 'background 0.25s, transform 0.25s',
};
const ctaSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '18px 32px', borderRadius: '999px',
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.15)',
  color: T.white,
  fontFamily: SANS, fontSize: '15px', fontWeight: 600,
  textDecoration: 'none',
  transition: 'border-color 0.25s, color 0.25s, transform 0.25s',
};

function Footer() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <footer style={{
      background: 'rgba(0,0,0,0.4)', color: T.white,
      padding: isMobile ? '60px 24px 36px' : 'clamp(80px, 10vw, 140px) clamp(28px, 6vw, 96px) 36px',
    }}>
      <div style={{ maxWidth: '1320px', margin: '0 auto' }}>
        {/* Massive wordmark */}
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontWeight: 300,
          fontSize: 'clamp(5rem, 15vw, 19rem)',
          lineHeight: 0.85, letterSpacing: '-0.05em',
          paddingRight: '0.1em',
          marginBottom: '60px',
          background: `linear-gradient(180deg, ${T.white} 0%, rgba(255,255,255,0.15) 100%)`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          DAGGPT
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.8fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
          gap: isMobile ? '40px' : '60px', marginBottom: '80px',
          paddingTop: '48px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: '20px', lineHeight: 1.4,
              color: 'rgba(255,255,255,0.7)',
              margin: '0 0 24px', maxWidth: '320px',
            }}>
              Blockchain Deployment of Creations for Provenance.
            </p>
            <a href="mailto:enterprise@daggpt.network" style={{
              fontFamily: MONO, fontSize: '12px',
              color: T.violetSoft, textDecoration: 'none',
              letterSpacing: '0.05em',
            }}>enterprise@daggpt.network</a>
          </div>

          {[
            { h: 'Product',   l: ['Workspace', 'Models', 'DAGCHAIN', 'Pricing', 'Enterprise'] },
            { h: 'Resources', l: ['Documentation', 'API', 'Blog', 'Status', 'Changelog'] },
            { h: 'Company',   l: ['About', 'Careers', 'Press', 'Privacy', 'Terms'] },
          ].map((col) => (
            <div key={col.h}>
              <h4 style={{
                fontFamily: MONO, fontSize: '10px', fontWeight: 700,
                letterSpacing: '0.26em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.35)',
                margin: '0 0 22px',
              }}>{col.h}</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {col.l.map((link) => (
                  <li key={link}>
                    <a href="#" style={{
                      fontFamily: SANS, fontSize: '14px', fontWeight: 400,
                      color: 'rgba(255,255,255,0.55)',
                      textDecoration: 'none',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = T.white}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}
                    >{link}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{
          paddingTop: '24px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px',
        }}>
          <span style={{
            fontFamily: MONO, fontSize: '11px',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.08em',
          }}>© 2025 DAGGPT — Anchored on DAGCHAIN</span>
          <div style={{ display: 'flex', gap: '20px' }}>
            {['Twitter', 'Discord', 'GitHub'].map(s => (
              <a key={s} href="#" style={{
                fontFamily: MONO, fontSize: '11px',
                color: 'rgba(255,255,255,0.5)',
                textDecoration: 'none',
                letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>{s}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export function GpuSection() {
  const ref = React.useRef(null);
  useReveal(ref, { stagger: 0.1 });
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const gpus = [
    { name: "NVIDIA H200 NVL", vram: "141GB", img: "h200-nvl.png" },
    { name: "NVIDIA H200", vram: "141GB", img: "h200.png" },
    { name: "NVIDIA H100 NVL", vram: "94GB", img: "h100-nvl.png" },
    { name: "NVIDIA H100 SXM", vram: "80GB", img: "h100-sxm.png" },
    { name: "NVIDIA H100", vram: "80GB", img: "h100.png" },
    { name: "NVIDIA B300", vram: "192GB", img: "b300.png" },
    { name: "NVIDIA B200", vram: "192GB", img: "b200.png" },
    { name: "NVIDIA A100 SXM4", vram: "80GB", img: "a100-sxm4.png" },
    { name: "NVIDIA A100", vram: "40GB", img: "a100.png" },
    { name: "NVIDIA L40S", vram: "48GB", img: "l40s.png" },
    { name: "NVIDIA L4", vram: "24GB", img: "l4.png" },
    { name: "AMD Instinct MI300X", vram: "192GB", img: "mi300x.png" },
    { name: "NVIDIA RTX 5090", vram: "32GB", img: "rtx-5090.png" },
    { name: "NVIDIA RTX 5080", vram: "16GB", img: "rtx-5080.png" },
    { name: "NVIDIA RTX 5070 Ti", vram: "16GB", img: "rtx-5070-ti.png" },
    { name: "NVIDIA RTX 5070", vram: "12GB", img: "rtx-5070.png" },
    { name: "NVIDIA RTX 5060 Ti", vram: "16GB", img: "rtx-5060-ti.png" },
    { name: "NVIDIA RTX 5060", vram: "12GB", img: "rtx-5060.png" },
    { name: "NVIDIA RTX 4090", vram: "24GB", img: "rtx-4090.png" },
    { name: "NVIDIA RTX 4080 SUPER", vram: "16GB", img: "rtx-4080s.png" },
    { name: "NVIDIA RTX 4080", vram: "16GB", img: "rtx-4080.png" },
    { name: "NVIDIA RTX 4070 Ti SUPER", vram: "16GB", img: "rtx-4070s-ti.png" },
    { name: "NVIDIA RTX 4070 Ti", vram: "12GB", img: "rtx-4070-ti.png" },
    { name: "NVIDIA RTX 4070 SUPER", vram: "12GB", img: "rtx-4070s.png" },
    { name: "NVIDIA RTX 4070", vram: "12GB", img: "rtx-4070.png" },
    { name: "NVIDIA RTX 4060 Ti", vram: "16GB", img: "rtx-4060-ti.png" },
    { name: "NVIDIA RTX 4060", vram: "8GB", img: "rtx-4060.png" },
    { name: "NVIDIA RTX 6000 Ada", vram: "48GB", img: "rtx-pro-6000-s.png" },
    { name: "NVIDIA RTX 6000 Ada WS", vram: "48GB", img: "rtx-pro-6000-ws.png" },
    { name: "NVIDIA RTX 4000 Ada", vram: "20GB", img: "rtx-pro-4000.png" }
  ];

  const row1 = gpus.slice(0, 10);
  const row2 = gpus.slice(10, 20);
  const row3 = gpus.slice(20, 30);

  const MarqueeRow = ({ items, reverse }) => (
    <div style={{
      display: 'flex',
      gap: '24px',
      paddingBottom: '24px',
      width: 'max-content',
      animation: `marquee 40s linear infinite ${reverse ? 'reverse' : 'normal'}`,
    }}>
      {[...items, ...items, ...items].map((gpu, i) => (
        <div key={i} style={{
          width: '280px',
          padding: '24px',
          borderRadius: '20px',
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={`/gpus/${gpu.img}`} alt={gpu.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ width: '100%', textAlign: 'left' }}>
            <div style={{ fontFamily: SERIF, fontSize: '16px', color: T.white, marginBottom: '4px' }}>
              {gpu.name}
            </div>
            <div style={{ fontFamily: MONO, fontSize: '12px', color: T.violetSoft, letterSpacing: '0.05em' }}>
              {gpu.vram} VRAM
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section ref={ref} style={{
      background: 'transparent',
      padding: isMobile ? '80px 0' : '120px 0',
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-33.333% - 8px)); }
        }
      `}</style>
      
      <div style={{
        maxWidth: '1320px', margin: '0 auto',
        padding: isMobile ? '0 24px' : '0 clamp(28px, 6vw, 96px)',
        marginBottom: '60px',
      }}>
        <div data-reveal>
          {/* SectionLabel removed */}
        </div>
        <h2 data-reveal style={{
          fontFamily: SERIF,
          fontWeight: 300,
          fontSize: 'clamp(2.6rem, 6vw, 6.5rem)',
          lineHeight: 0.97,
          letterSpacing: '-0.035em',
          color: T.white,
          margin: '0 0 28px',
        }}>
          Rent a GPU<br />
          <em style={{
            fontStyle: 'italic', fontWeight: 400,
            background: `linear-gradient(135deg, ${T.violetSoft}, ${T.violet})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            display: 'inline-block',
            paddingRight: '0.12em',
            paddingBottom: '0.08em',
          }}>as a Service.</em>
        </h2>
        <p data-reveal style={{
          fontFamily: SANS,
          fontSize: 'clamp(15px, 1.2vw, 18px)',
          lineHeight: 1.7,
          color: 'rgba(255,255,255,0.55)',
          maxWidth: '480px', margin: '0',
          fontWeight: 400,
        }}>
          Train your models with uncompromised compute power. Access the world's most advanced accelerators instantly, billed by the hour, with dedicated bandwidth and massive VRAM.
        </p>
      </div>

      <div data-reveal style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, bottom: 0, width: '150px',
          background: 'linear-gradient(to right, rgba(10,10,10,0.8) 0%, transparent 100%)',
          zIndex: 2, pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: '150px',
          background: 'linear-gradient(to left, rgba(10,10,10,0.8) 0%, transparent 100%)',
          zIndex: 2, pointerEvents: 'none'
        }} />
        
        <MarqueeRow items={row1} />
        <MarqueeRow items={row2} reverse={true} />
        <MarqueeRow items={row3} />
      </div>
    </section>
  );
}
